import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  BasicReadingTestPoint,
  BasicReadingTestData
} from '../../../hooks/useCalibrationRuntime/types';

const computeResult = (
  measured: number | null,
  nominalValue: number,
  tolerance: number
): 'Pass' | 'Fail' | null => {
  if (measured === null) return null;
  const error = Number((measured - nominalValue).toFixed(10));
  return Math.abs(error) <= tolerance ? 'Pass' : 'Fail';
};

const NOMINAL_MATCH_TOLERANCE = 1e-6;

const nominalMatches = (a: number, b: number): boolean => {
  const diff = Math.abs(a - b);
  const scale = Math.max(Math.abs(a), Math.abs(b));
  return diff <= NOMINAL_MATCH_TOLERANCE * Math.max(scale, 1e-12);
};

export type LiveMeasurement = {
  nominal: number;
  field: 'asFound' | 'asLeft';
  value: number;
};

const computeStatus = (
  points: BasicReadingTestPoint[]
): BasicReadingTestData['status'] => {
  if (points.length === 0) return 'NotStarted';
  const anyStarted = points.some(p => p.asFound !== null || p.asLeft !== null);
  if (!anyStarted) return 'NotStarted';
  const allComplete = points.every(
    p => p.asFound !== null && p.asLeft !== null
  );
  if (!allComplete) return 'InProgress';
  const anyFail = points.some(
    p => p.asFoundResult === 'Fail' || p.asLeftResult === 'Fail'
  );
  return anyFail ? 'Fail' : 'Pass';
};

const buildTestData = (
  points: BasicReadingTestPoint[]
): BasicReadingTestData => ({
  status: computeStatus(points),
  startedAt: null,
  completedAt: null,
  testPoints: points
});

type UseBasicReadingReturn = {
  testPoints: BasicReadingTestPoint[];
  addTestPoint: () => void;
  deleteTestPoint: (id: string) => void;
  updateTestPoint: (
    id: string,
    field: 'asFound' | 'asLeft',
    value: number | null
  ) => void;
};

export type FieldOverrideHandler = (
  pointId: string,
  field: 'asFound' | 'asLeft',
  value: number | null,
  data: BasicReadingTestData
) => void;

export const useBasicReading = (
  initialPoints: BasicReadingTestPoint[],
  onSave?: (data: BasicReadingTestData) => void,
  liveMeasurement?: LiveMeasurement | null,
  onFieldOverride?: FieldOverrideHandler
): UseBasicReadingReturn => {
  const [testPoints, setTestPoints] =
    useState<BasicReadingTestPoint[]>(initialPoints);

  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const onFieldOverrideRef = useRef(onFieldOverride);
  useEffect(() => {
    onFieldOverrideRef.current = onFieldOverride;
  }, [onFieldOverride]);

  useEffect(() => {
    if (!liveMeasurement) return;

    const { nominal, field, value } = liveMeasurement;
    const overriddenField =
      field === 'asFound' ? 'asFoundOverridden' : 'asLeftOverridden';
    const resultField = field === 'asFound' ? 'asFoundResult' : 'asLeftResult';

    setTestPoints(prev => {
      let changed = false;
      const updated = prev.map(p => {
        if (!nominalMatches(p.nominalValue, nominal) || p[overriddenField]) {
          return p;
        }
        changed = true;
        return {
          ...p,
          [field]: value,
          [resultField]: computeResult(value, p.nominalValue, p.tolerance)
        };
      });
      return changed ? updated : prev;
    });
  }, [liveMeasurement]);

  const addTestPoint = useCallback(() => {
    setTestPoints(prev => {
      const updated = [
        ...prev,
        {
          id: crypto.randomUUID(),
          nominalValue: 0,
          tolerance: 0.1,
          asFound: null,
          asLeft: null,
          asFoundResult: null,
          asLeftResult: null
        }
      ];
      onSaveRef.current?.(buildTestData(updated));
      return updated;
    });
  }, []);

  const deleteTestPoint = useCallback((id: string) => {
    setTestPoints(prev => {
      const updated = prev.filter(p => p.id !== id);
      onSaveRef.current?.(buildTestData(updated));
      return updated;
    });
  }, []);

  const updateTestPoint = useCallback(
    (id: string, field: 'asFound' | 'asLeft', value: number | null) => {
      setTestPoints(prev => {
        const updated = prev.map(p => {
          if (p.id !== id) return p;
          const resultField =
            field === 'asFound' ? 'asFoundResult' : 'asLeftResult';
          const overriddenField =
            field === 'asFound' ? 'asFoundOverridden' : 'asLeftOverridden';
          return {
            ...p,
            [field]: value,
            [resultField]: computeResult(value, p.nominalValue, p.tolerance),
            [overriddenField]: true
          };
        });
        if (onFieldOverrideRef.current) {
          onFieldOverrideRef.current(id, field, value, buildTestData(updated));
        } else {
          onSaveRef.current?.(buildTestData(updated));
        }
        return updated;
      });
    },
    []
  );

  return { testPoints, addTestPoint, deleteTestPoint, updateTestPoint };
};
